import React, { act, useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from 'ag-grid-react';

import { getJobs } from "../apis/jobApi";
import { getNumComparators, getEnumComparators, getTextComparators, getDateComparators } from "../library/comparators";
import { IdTableCellLink } from "../components/Table/IdTableCellLink";
import { JobsRowActions } from "../components/Jobs/JobActions";
import { SubmitJobModal } from '../components/Jobs/SubmitJobModal';
import { BatchActionAccordion } from '../components/BatchActionAccordion';
import { BatchJobActions } from "../components/Jobs/BatchJobActions";
import { SaveJobTableModal } from "../components/Table/SaveTableSettingsModal";
import { dateFormatter, dateTimeFormatter } from "../library/formatters";

import { Row, Col, Alert, Container } from 'react-bootstrap'; // Bootstrap
import { UpdateService } from "../services/UpdateService";
import { CustomStateFilter } from "../components/Table/CustomFilters/CustomStateFilter";
import { getSettings, saveJobSettings } from "../apis/settingApi";
import { SaveTableSettings } from "../components/Table/SaveTableSettings";
import { SubmittedByCell } from "../components/Jobs/SubmittedByCell";
import { CustomStateCell } from "../components/Table/CustomStateCell";

export const JobsPage = () => {
  const gridRef = useRef();
  const [gridKey, setGridKey] = useState('grid-key-${Math.random()}');
  const [gridParams, setGridParams] = useState(null);
  const pageSizeOptions = [15, 25, 50, 100];
  const defaultPageSize = 25;
  const [open, setOpen] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);   
  const [errorMessage, setErrorMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(true);
  const numComparators = getNumComparators(); 
  const textComparators = getTextComparators(); 
  const dateComparators = getDateComparators();
  
  const stateMessageGetter = (params) => {
    return params?.data?.stateMessage == null || params?.data?.stateMessage === "" ? '- No Status Message -' : params.data.stateMessage;
  }

  const stateOptions = ['Draft', 'Queueing', 'Queued', 'Pausing', 'Paused', 'GeneratingTasks', 'TasksGenerated','ExecutingTasks', 'TasksExecuted', 'GatheringResults', 'ResultsGathered', 
    'UploadingResults','Completed', 'Failed', 'Archiving', 'Archived', 'Purging', 'Purged'];


  const [colDefs, setColDefs] = useState([
    {field: "id", headerName: "Job Id", filter: false, sortable: true, filterParams:{buttons: ["clear"]}, cellRenderer: IdTableCellLink, cellRendererParams: { gridRef: gridRef, path: "/jobs/"},  sort: 'desc', flex:1},
    {field: "description", filter: 'agTextColumnFilter', sortable: true, filterParams:{buttons: ["clear"], filterOptions : textComparators, maxNumConditions: 1},flex:1.2, tooltipField: "description"},
    {field: "state", headerName: "State", filter: CustomStateFilter, cellRenderer:CustomStateCell ,sortable: false,  cellRendererParams: { gridRef: gridRef}, filterParams: {title: "State Filter", buttons: ["clear"], filterOptions: stateOptions}, tooltipValueGetter: stateMessageGetter},
    {field: "priority",filter: 'agNumberColumnFilter', sortable: true, filterParams:{buttons: ["clear"],filterOptions : numComparators, maxNumConditions: 1},flex:1},
    {field: "created", filter: 'agDateColumnFilter', 
      sortable: true, valueFormatter: dateTimeFormatter, 
      filterParams:{buttons: ["clear"],filterOptions : dateComparators, maxNumConditions: 1, maxValidYear:9999},flex:1, 
    },
    {field: "created", headerName: "Submitted By", sortable: false, filter: true,  cellRenderer:SubmittedByCell,flex:1, cellRendererParams: { gridRef: gridRef}, suppressHeaderFilterButton: true, suppressHeaderMenuButton: true, filterParams:{filterOptions : textComparators, maxNumConditions: 1} },
    {field: "billCode", headerName: "Bill Code", filter: 'agTextColumnFilter', filterParams:{buttons: ["clear"], filterOptions : textComparators, maxNumConditions: 1},flex:1.2, tooltipField: "description"},
    {field: "actions", editable: false, filter: false, sortable: false, headerName: "Actions", cellRenderer: JobsRowActions, cellRendererParams: { gridRef: gridRef, setErrorMessage: setErrorMessage },flex:1.1}
  ]);
  
  const defaultColDef = useMemo(() => {
    return {
      flex: 1
    };
  }, []);

  const rowSelection = useMemo(() => {
    return {
      mode: "multiRow",
    };
  }, []);

  const onGridReady = useCallback( async (params) => {
    const dataSource = {
      getRows: async function (params) {
        setGridParams(params);
        const filters = params.filterModel;
        setIsRefreshing(true);
        const response = await getJobs(filters, params.sortModel, params.startRow, params.endRow - params.startRow)
          .then(response => {  
              setIsLoading(false);
              setIsRefreshing(false);
              return {total: response.total, data: response.data.map((fileRow => fileRow))};
          })
          .catch(error => {
            setIsRefreshing(false);
            setIsLoading(false);
            
            if (error.status === 403)
            {
              setErrorMessage({alertType: "danger", message: 'You do not have permission to view jobs. Please contact your administrator.'});
            }
            else if (error.status >= 500 && error.status < 600)
            {
              if(error.status === 503 || error.status === 500)
              {
                setErrorMessage({alertType: "warning", message: 'The service is currently unavailable. Please try again later.'});
              }
              else
              {
                setErrorMessage({alertType: "danger", message: error.message});
              }
            }
            else
            {
              setErrorMessage({alertType: "danger", message: error.message});
            }
          });

        if (!response) return;
        // if on or after the last page, work out the last row.
        let lastRow = response.total;
  
        params.successCallback(response.data, lastRow);
      }
    };
    
    params.api.setGridOption('datasource', dataSource);
  });

  const onSelectionChanged = useCallback(() => {
    const selectedRows = gridRef.current.api.getSelectedRows();
    if(selectedRows.length > 0)
    {
      setOpen(true);
    }
    else
    {
      setOpen(false);
    }
  }, []);

  const openJobModal = () => {
    setShowJobModal(true);
  }


  const handleRefreshJobs = () => {
    gridRef.current.api.refreshInfiniteCache();
  }

  return (<>
  {
      errorMessage && (
        <Alert variant={errorMessage.alertType} onClose={() => setErrorMessage(false)} dismissible>
            {errorMessage.message}
        </Alert>
      )
  }
  {
    isLoading && (
      <Container fluid className="loading-container">
        <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}>
                <span className="visually-hidden">Fetching jobs...</span>
            </div>
            <div className="mt-3">
                <h5>Fetching Jobs</h5>
                <p className="text-muted">Please wait while we fetch the jobs...</p>
            </div>
        </div>
      </Container>
    )
    
  }
  {
    !isLoading &&(
      <Row>
        <Col>
            <h3 className="display-6 job-page-header" >Jobs</h3>
        </Col>
        <Col className="d-flex justify-content-end align-items-end">
            <BatchActionAccordion aria-expanded={open} open={open} batchAction={<BatchJobActions gridRef={gridRef} setOpen={setOpen} setGridKey={setGridKey}/>}/>
            <SubmitJobModal show={showJobModal} setShow={setShowJobModal}/>
            <SaveTableSettings gridParams={gridParams} gridRef={gridRef} tableType={"jobs"}/>
        </Col>
      </Row>
    )
  }

  <Row style={{display:isLoading && 'none'}}>
    <Col className={"jobs-grid-row"}>
      <div className={"ag-theme-quartz"}>
        <AgGridReact
          suppressClickEdit={true}
          ref={gridRef}
          key={gridKey}
          pagination={true}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          rowSelection={rowSelection}
          rowModelType={'infinite'}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          paginationPageSize={defaultPageSize}
          paginationPageSizeSelector={pageSizeOptions}
          cacheBlockSize={defaultPageSize}
          cacheOverflowSize={2}
          maxConcurrentRequests={2}
          infiniteInitialRowCount={1}
          maxBlocksInCache={1}
        />
      </div>
    </Col>
  </Row>
  <UpdateService updateDataFunction={handleRefreshJobs} active={!isRefreshing} show={!isLoading}/>      
  </>
  );
};
  